import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';

/** Read by Posterbuilder::loadPosterEditor into `posterEditorConfig.shouldOpenTimelineOnLoad`. */
const OPEN_TIMELINE_ON_LOAD_QUERY_PARAM = 'tl';

/** `shouldOpenTimeline` is for a design just composed on one timeline: the editor opens with the timeline shown. */
export const getEditPosterURL = (hash: string, shouldOpenTimeline = false): string => {
  const editUrl = window.PMW.util.site_url(`posterbuilder/load/${hash}`);
  return shouldOpenTimeline ? `${editUrl}?${OPEN_TIMELINE_ON_LOAD_QUERY_PARAM}=1` : editUrl;
};

/** Only a video page can show the timeline; a stale `tl=1` on a static design is ignored. */
export const shouldOpenTimelineOnLoad = (poster: Poster): boolean => {
  return window.posterEditor?.config?.shouldOpenTimelineOnLoad === true && poster.getCurrentPage().isVideo();
};

// Intentionally not "pmw"-prefixed — PMW's per-release cache wipe deletes every "pmw*" key, which would reset this device preference on each deploy.
const MOBILE_TIMELINE_OPEN_STORAGE_KEY = 'editor_timeline_mobile_open';

/** A phone remembers whether the user last left the timeline open or closed; the desktop toggle is not persisted. */
export const persistMobileTimelineOpenPreference = (isOpen: boolean): void => {
  if (!isEditorMobileVariant()) {
    return;
  }
  try {
    localStorage.setItem(MOBILE_TIMELINE_OPEN_STORAGE_KEY, String(isOpen));
  } catch {
    // localStorage may be unavailable
  }
};

/** Reopens the mobile timeline the way the user last left it, as long as the page can show one. */
export const shouldRestoreMobileTimelineOnLoad = (poster: Poster): boolean => {
  if (!isEditorMobileVariant()) {
    return false;
  }
  try {
    return localStorage.getItem(MOBILE_TIMELINE_OPEN_STORAGE_KEY) === 'true' && poster.getCurrentPage().isVideo();
  } catch {
    return false;
  }
};

/**
 * Rewrites the browser URL to the plain poster-load path without navigating, so a page refresh loads the
 * poster directly instead of re-running whatever flow (AI chat image add, editable-layers conversion) got
 * the editor here.
 */
export const replacePosterLoadUrl = (hashedId: string): void => {
  window.history.replaceState(null, '', getEditPosterURL(hashedId));
};

export const getDuplicatePosterURL = (hash: string): string => {
  return window.PMW.util.site_url(`posterbuilder/copy/${hash}`);
}

export const isInReviewOrGalleryTemplate = (): boolean => {
  return !!window.posterEditor?.whiteboard?.isGalleryTemplate() || !!window.posterEditor?.config?.isInReview;
};
